import { useRef } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '@/components/UI/InputTextField';
import CountrySelectField from '@/components/UI/CountrySelectField';
import TextareaField from '@/components/UI/TextareaField';
import { RoundButton } from '@/components/UI/RoundButton';
import { RoundLink } from '@/components/UI/RoundLink';
import { PhoneNumberField } from '@/components/UI/PhoneNumberField';
import { checkForValidFile } from '@/helpers/utils';
import { PHOTO_ALLOWED_FORMATS } from '@/helpers/common';
import PhotoUpload from '../../PhotoUpload';
import { ROLES } from '@/helpers/constants';
import './index.scss';

const VALIDATION_SCHEMA = t =>
  Yup.object({
    first_name: Yup.string()
      .max(40, t('contact_info_form.first_name_exceeds_limit'))
      .required(t('contact_info_form.first_name_required')),
    last_name: Yup.string()
      .max(40, t('contact_info_form.last_name_exceeds_limit'))
      .required(t('contact_info_form.last_name_required')),
    citizenship: Yup.string().required(t('contact_info_form.citizenship_required')),
    company_name: Yup.string().max(256, t('contact_info_form.company_name_exceeds_limit')),
    description: Yup.string().max(1000, t('contact_info_form.exceeded_limit_1000_symbols')),
    about: Yup.string().max(1000, t('contact_info_form.exceeded_limit_1000_symbols')),
    phone: Yup.number().required(t('contact_info_form.phone_number_required')),
    is_valid_phone: Yup.bool()
      .oneOf([true], t('contact_info_form.invalid_phone_number'))
      .required(),
  });

const ContactInfoForm = ({
  userLocation,
  user,
  onSubmit,
  disableSkip,
  disableTitle,
  defaultSubmit,
  role,
  onCancel,
}) => {
  const { t } = useTranslation('auth');
  const editorRef = useRef(null);

  const setEditorRef = ref => {
    editorRef.current = ref;
  };

  const getInitialValues = (user, location) => ({
    first_name: (user && user.first_name) || '',
    last_name: (user && user.last_name) || '',
    citizenship: (user && user.citizenship) || '',
    citizenship_code: (user && user.citizenship_code) || '',
    is_valid_phone: true,
    phone: (user && user.phone) || '',
    country_code: (location && location.countryCode) || '',
    company_name: (user && user.company && user.company.name) || '',
    description: (user && user.company && user.company.description) || '',
    about: (user && user.description) || '',
    resume: (user && user.cv) || null,
    photo: (user && user.photo) || null,
  });

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(user, userLocation)}
      validationSchema={VALIDATION_SCHEMA(t)}
      onSubmit={(values, formikBag) => onSubmit(values, formikBag, editorRef.current)}
    >
      {({ values, errors, touched, setFieldError, handleChange, handleSubmit, setFieldValue }) => (
        <form className="contact-info-form" onSubmit={handleSubmit}>
          {!disableTitle && (
            <h1 className="contact-info-form__title f-500">
              {t('contact_info_form.contact_information')}
            </h1>
          )}
          <InputTextField
            label={t('contact_info_form.first_name_label', { required: '*' })}
            name="first_name"
            value={values.first_name}
            onChange={handleChange}
            onClear={() => setFieldValue('first_name', '')}
            error={errors.first_name && touched.first_name && errors.first_name}
            className="contact-info-form__row"
          />
          <InputTextField
            label={t('contact_info_form.last_name_label', { required: '*' })}
            name="last_name"
            value={values.last_name}
            onChange={handleChange}
            onClear={() => setFieldValue('last_name', '')}
            error={errors.last_name && touched.last_name && errors.last_name}
            className="contact-info-form__row"
          />
          <CountrySelectField
            label={t('contact_info_form.citizenship_label', { required: '*' })}
            name="citizenship"
            value={values.citizenship_code}
            onChange={(country, code) => {
              setFieldValue('citizenship', country);
              setFieldValue('citizenship_code', code);
            }}
            error={errors.citizenship && touched.citizenship && errors.citizenship}
            className="contact-info-form__row"
          />
          <PhoneNumberField
            label={t('contact_info_form.phone_number_label', { required: '*' })}
            name="phone"
            value={values.phone}
            countryCode={values.country_code}
            onChange={phone => setFieldValue('phone', phone)}
            onClear={() => setFieldValue('phone', '')}
            error={
              (errors.phone || errors.is_valid_phone) &&
              (touched.phone || touched.is_valid_phone) &&
              (errors.phone || errors.is_valid_phone)
            }
            containerID="phone-input"
            isValid={values.is_valid_phone}
            formikBag={{ setFieldValue, touched }}
            className="contact-info-form__row"
          />
          <InputTextField
            label={t('contact_info_form.company_name_label')}
            name="company_name"
            value={values.company_name}
            onChange={handleChange}
            onClear={() => setFieldValue('company_name', '')}
            error={errors.company_name && touched.company_name && errors.company_name}
            disabled={role === ROLES.co_founder}
            className="contact-info-form__row"
            readOnly={role === ROLES.co_founder}
          />

          {role !== ROLES.co_founder && (
            <TextareaField
              label={t('contact_info_form.company_description_label')}
              name="description"
              minRows={2}
              value={values.description}
              onChange={handleChange}
              limit={1000}
              error={errors.description && touched.description && errors.description}
              disabled={role === ROLES.co_founder}
              className="contact-info-form__row"
            />
          )}

          <TextareaField
            label={t('contact_info_form.about_you_label')}
            name="about"
            minRows={2}
            value={values.about}
            onChange={handleChange}
            error={errors.about && touched.about && errors.about}
            limit={1000}
            className="contact-info-form__row"
          />
          <p className="contact-info-form__about-intro">
            {t('contact_info_form.introduce_yourself')}
          </p>
          {/*<CVUpload*/}
          {/*  label={t('contact_info_form.upload_your_cv')}*/}
          {/*  name="resume"*/}
          {/*  value={values.resume}*/}
          {/*  error={errors.resume}*/}
          {/*  onChange={e => {*/}
          {/*    const file = e.target.files[0];*/}
          {/*    const { isValid, type, size } = checkForValidFile(*/}
          {/*      file,*/}
          {/*      CV_ALLOWED_FORMATS,*/}
          {/*      12582912*/}
          {/*    );*/}
          {/*    !isValid &&*/}
          {/*      size &&*/}
          {/*      setFieldError('resume', t('contact_info_form.file_size_too_big'));*/}
          {/*    !isValid &&*/}
          {/*      type &&*/}
          {/*      setFieldError(*/}
          {/*        'resume',*/}
          {/*        t('contact_info_form.incorrect_file_type_cv')*/}
          {/*      );*/}
          {/*    isValid && setFieldValue('resume', file);*/}
          {/*  }}*/}
          {/*  onRemove={() => setFieldValue('resume', null)}*/}
          {/*  className="contact-info-form__row"*/}
          {/*/>*/}
          <PhotoUpload
            label={t('contact_info_form.upload_your_photo')}
            name="photo"
            value={values.photo}
            onChange={e => {
              const file = e.target.files[0];
              const { isValid, type, size } = checkForValidFile(
                file,
                PHOTO_ALLOWED_FORMATS,
                12582912,
              );
              !isValid && size && setFieldError('photo', t('contact_info_form.file_size_too_big'));
              !isValid &&
                type &&
                setFieldError('photo', t('contact_info_form.incorrect_file_type_photo'));
              isValid && setFieldValue('photo', file);
            }}
            setFieldValue={setFieldValue}
            setEditorRef={setEditorRef}
            onRemove={() => setFieldValue('photo', null)}
            error={errors.photo}
            className="contact-info-form__row"
          />

          <div className="contact-info-form__buttons">
            {onCancel && (
              <RoundButton
                label={t('contact_info_form.cancel')}
                type="button"
                onClick={onCancel}
                className="contact-info-form__button"
              />
            )}
            <RoundButton
              label={defaultSubmit ? t('contact_info_form.save') : t('contact_info_form.continue')}
              type="submit"
              onSubmit={handleSubmit}
              fillBackground
              fullWidth
              withArrow={!defaultSubmit}
              className="contact-info-form__button"
            />
            {!disableSkip && (
              <RoundLink
                label={t('contact_info_form.continue_later')}
                path="/"
                className="contact-info-form__link"
              />
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default ContactInfoForm;
